import { cleanSequence } from "@/lib/science/sequence";

export interface GcSkewResult {
  /** window index positions (1-based window number) */
  positions: number[];
  /** per-window GC skew (G−C)/(G+C) */
  skew: number[];
  /** cumulative sum of per-window skew */
  cumulative: number[];
  /** overall skew across the whole sequence */
  overall: number;
  windowSize: number;
  windows: number;
}

/**
 * GC skew (G−C)/(G+C) over non-overlapping windows, plus cumulative skew.
 * U is treated as T (and ignored for skew, which uses only G and C).
 */
export function gcSkew(input: string, windowSize = 100): GcSkewResult {
  const seq = cleanSequence(input);
  const w = Math.max(1, Math.min(windowSize, seq.length || 1));

  const positions: number[] = [];
  const skew: number[] = [];
  const cumulative: number[] = [];
  let running = 0;
  let idx = 0;

  for (let i = 0; i + w <= seq.length; i += w) {
    const win = seq.slice(i, i + w);
    let g = 0;
    let c = 0;
    for (const ch of win) {
      if (ch === "G") g++;
      else if (ch === "C") c++;
    }
    const s = g + c > 0 ? (g - c) / (g + c) : 0;
    idx++;
    positions.push(idx);
    skew.push(s);
    running += s;
    cumulative.push(running);
  }

  // Overall skew across the whole sequence.
  let gTot = 0;
  let cTot = 0;
  for (const ch of seq) {
    if (ch === "G") gTot++;
    else if (ch === "C") cTot++;
  }
  const overall = gTot + cTot > 0 ? (gTot - cTot) / (gTot + cTot) : 0;

  return {
    positions,
    skew,
    cumulative,
    overall,
    windowSize: w,
    windows: positions.length,
  };
}
